import React, { ReactElement, useEffect } from 'react';

import { CTA, Garage, Pin, Spinner } from '@vw-marketing/us-components';
import { useFlags } from 'launchdarkly-react-client-sdk';

import { applyTextTransformations } from '../../utils/applyTextTransformations';
import {
  NavigationContainer,
  NavigationContent,
  NavigationDivider,
  NavigationItems,
  StyledDealerContainer,
} from './styles';
import {
  onVWBasicStepLoadEvent,
  onVWBasicNavigationButtonClickEvent,
  onVWBasicLinkClickWithProcessEvent,
} from '../../tagging/tagging-helpers';
import { useAuth } from '../../../app/providers/AuthContext';
import { useDealer } from '../../../app/providers/DealerContext';
import { useFeatureServices } from '../../hooks/use-feature-services';
import { useHistory } from '../../hooks/use-history';
import { useMyVwContext } from '../../hooks/use-my-vw-context';
import { useTrackingManager } from '../../hooks/use-tracking-manager';
import { useUser } from '../../../app/providers/UserContext';
import { useVehicle } from '../../../app/providers/VehicleContext';
import NavigationItem from './NavigationItem';
import PinFilled from '../icons/PinFilled';
import UserProfile from '../user-profile/UserProfile';

export const Navigation = ({
  items,
  onOpenGarageLayer,
  onOpenDealerLayer,
}: {
  items: {
    externalLink?: string;
    icon?: ReactElement<{ filled: boolean }>;
    iconWhenSelected?: ReactElement<{ filled: boolean }>;
    labelKey: string;
    path?: string;
    titleLabelKey?: string;
  }[];
  onOpenGarageLayer: () => void;
  onOpenDealerLayer: () => void;
}) => {
  const {
    myvwFaRolloutManageDealer,
    myVwFaRolloutVehicleManagement,
  } = useFlags();

  const { isLoadingPreferredDealer, userPreferredDealer } = useDealer();
  const { logout, shouldTrackInitialLoad, markInitialTrackingComplete } =
    useAuth();
  const { translationHandler } = useMyVwContext();
  const { user } = useUser();
  const { 'locale-service': localeService } = useFeatureServices();
  const isCanada = localeService?.countryCode === 'CA';
  const { userPreferredVehicle, isLoadingPreferredVehicle } = useVehicle();
  const { vehicles = [] } = user || {};

  const isGarageAvailable: boolean = vehicles.length > 1;
  const history = useHistory();
  const trackingManager = useTrackingManager();
  // Track step load event when component mounts - only once per session
  useEffect(() => {
    if (shouldTrackInitialLoad && trackingManager) {
      try {
        onVWBasicStepLoadEvent(trackingManager, {
          Name: 'Login',
          StepName: 'Account',
          StepNumber: '1',
        });

        // Mark tracking as complete to prevent duplicate events
        markInitialTrackingComplete();
      } catch (error) {
        console.warn('Error tracking initial step load event:', error);
        // Still mark as complete to prevent retries
        markInitialTrackingComplete();
      }
    }
  }, [shouldTrackInitialLoad, trackingManager, markInitialTrackingComplete]);

  /**
   * Maps navigation item paths to their corresponding step numbers
   * Based on the journey flow: 2=selected vehicle, 3=find dealer/selected dealer,
   * 4=account settings, 5=vehicle management, 6=volkswagen financial services
   */
  const getStepNumberForPath = (path?: string, labelKey?: string): string => {
    if (!path && !labelKey) return '1';

    // Map specific paths to step numbers
    if (path === '/vehicle-management' || labelKey?.includes('vehicle'))
      return '5';
    if (path === '/manage-dealer' || labelKey?.includes('dealer')) return '3';
    if (path === '/account-settings' || labelKey?.includes('account'))
      return '4';

    // Check for financial services in labelKey
    if (labelKey?.toLowerCase().includes('financial')) return '6';

    // Default to step 2 for other navigation items
    return '2';
  };

  /**
   * Gets the step name based on the step number
   */
  const getStepNameForNumber = (stepNumber: string): string => {
    switch (stepNumber) {
      case '2':
        return 'Selected Vehicle';
      case '3':
        return 'Find Dealer';
      case '4':
        return 'Account Settings';
      case '5':
        return 'Vehicle Management';
      case '6':
        return 'Financial Services';
      default:
        return 'Account';
    }
  };

  /**
   * Handles navigation button click tracking with process and link information
   * @param labelKey - The translation key for the CTA
   * @param path - The navigation path (optional)
   * @param externalLink - External link URL (optional)
   */
  const handleNavigationButtonClick = (
    labelKey: string,
    path?: string,
  ) => {
    const stepNumber = getStepNumberForPath(path, labelKey);
    const stepName = getStepNameForNumber(stepNumber);
    const ctaName = translationHandler(labelKey);

    const processData = {
      Name: ctaName,
      StepName: stepName,
      StepNumber: stepNumber,
    };
    const linkData = { name: ctaName };

    if (isCanada) {
      onVWBasicLinkClickWithProcessEvent(trackingManager, processData, linkData);
    } else {
      onVWBasicNavigationButtonClickEvent(trackingManager, processData, linkData);
    }
  };

  /**
   * Handles logout button click with tracking
   */
  const handleLogoutClick = () => {
    try {
      // Track logout button click
      const logoutText = translationHandler('logOut');

      onVWBasicNavigationButtonClickEvent(
        trackingManager,
        {
          Name: 'Login',
          StepName: 'Account',
          StepNumber: '1',
        },
        {
          name: logoutText,
        },
      );
    } catch (error) {
      console.warn('Error tracking logout button click:', error);
    }

    // Proceed with logout
    logout();
  };

  const handleGarageClick = () => {
    // Track garage button click
    handleNavigationButtonClick(
      'garage.navigation.item.label',
      '/vehicle-management',
    );

    // Track VWBasic_Step_Load event after user clicks on Selected Vehicle item
    // as specified: process.Name = "Selected Vehicle", process.StepName = "Garage", process.StepNumber = "2"
    try {
      onVWBasicStepLoadEvent(trackingManager, {
        Name: 'Selected Vehicle',
        StepName: 'Garage',
        StepNumber: '2',
      });
    } catch (error) {
      console.warn('Error tracking garage step load event:', error);
    }

    onOpenGarageLayer();
  };

  const handleManageDealerClick = () => {
    // Track dealer button click
    const dealerLabelKey = userPreferredDealer
      ? 'accountSettings.manageDealer.yourDealer.label'
      : 'accountSettings.manageDealer.none.dealer.label2';

    handleNavigationButtonClick(dealerLabelKey, '/manage-dealer');

    if (userPreferredDealer) {
      history.push('/manage-dealer');
    } else {
      onOpenDealerLayer();
    }
  };

  const SpinnerContainer = () => {
    return (
      <div style={{ width: '100%', display: 'flex', justifyContent: 'center' }}>
        <Spinner />
      </div>
    );
  };

  return (
    <NavigationContainer>
      <NavigationContent>
        <UserProfile name={user ? user?.firstName : ''} greeting="Hello" />
        <NavigationDivider />
        <NavigationItems>
          {myVwFaRolloutVehicleManagement && (
            <>
              {isLoadingPreferredVehicle ? (
                <SpinnerContainer />
              ) : isGarageAvailable && userPreferredVehicle ? (
                <NavigationItem
                  aria-label={translationHandler(
                    'garage.navigation.item.label',
                  )}
                  icon={<Garage />}
                  key={translationHandler('garage.navigation.item.label')}
                  onClick={() => {
                    handleGarageClick();
                  }}
                  text={applyTextTransformations(
                    `${userPreferredVehicle?.modelYear ?? ''} ${userPreferredVehicle?.modelName ?? ''}`,
                    [
                      userPreferredVehicle?.modelYear ?? '',
                      userPreferredVehicle?.modelName ?? '',
                    ],
                  )}
                  title={translationHandler('garage.navigation.item.label')}
                />
              ) : null}
            </>
          )}
          {myvwFaRolloutManageDealer && (
            <>
              {isLoadingPreferredDealer ? (
                <SpinnerContainer />
              ) : userPreferredVehicle ? (
                <StyledDealerContainer>
                  <NavigationItem
                    path="/manage-dealer"
                    onClick={() => {
                      handleManageDealerClick();
                    }}
                    aria-label={translationHandler(
                      userPreferredDealer
                        ? 'accountSettings.manageDealer.yourDealer.label'
                        : 'accountSettings.manageDealer.none.dealer.label2',
                    )}
                    // @ts-ignore
                    text={
                      userPreferredDealer
                        ? applyTextTransformations(userPreferredDealer.name, [
                          userPreferredDealer.name,
                        ])
                        : translationHandler(
                          'accountSettings.manageDealer.none.dealer.label2',
                        )
                    }
                    icon={<Pin />}
                    iconWhenSelected={<PinFilled />}
                    key={'accountSettings.manageDealer.none.dealer.label2'}
                    title={translationHandler(
                      userPreferredDealer
                        ? 'accountSettings.manageDealer.yourDealer.label'
                        : 'accountSettings.manageDealer.none.dealer.label1',
                    )}
                  />
                  <NavigationDivider />
                </StyledDealerContainer>
              ) : null}
            </>
          )}
          {items.map((item) => (
            <NavigationItem
              path={item.path}
              onClick={() => {
                // Track navigation button click with process and link information
                handleNavigationButtonClick(
                  item.labelKey,
                  item.path,
                );

                // Handle navigation
                if (item?.path) history.push(item.path);
              }}
              aria-label={translationHandler(item.labelKey)}
              text={translationHandler(item.labelKey)}
              icon={item.icon}
              externalLink={item.externalLink}
              key={item.labelKey}
              title={translationHandler(item?.titleLabelKey || '')}
              iconWhenSelected={item.iconWhenSelected}
            />
          ))}
        </NavigationItems>
        <NavigationDivider hideOnMobile />
      </NavigationContent>
      <CTA
        tag="button"
        emphasis="secondary"
        aria-label={translationHandler('logOut')}
        stretchContent
        onClick={handleLogoutClick}
      >
        {translationHandler('logOut')}
      </CTA>
    </NavigationContainer>
  );
};